import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from '../config';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function ResetPassword() {
  const query = useQuery();
  const token = query.get("token");
  const email = query.get("email");
  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [message, setMessage] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [toggledPassword, setToggledPassword] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;
    async function verify() {
      if (!token || !email) {
        if (!isMounted) return;
        setMessage("Invalid reset link.");
        setValid(false);
        setLoading(false);
        return;
      }

      try {
        const res = await axios.get(`${API_BASE_URL}/api/auth/verify-reset?token=${encodeURIComponent(token)}&email=${encodeURIComponent(email)}`);
        if (!isMounted) return;
        if (res.data?.valid) {
          setValid(true);
          setMessage("");
        } else {
          setValid(false);
          setMessage(res.data?.message || "Invalid or expired reset link.");
        }
      } catch (err) {
        if (!isMounted) return;
        const serverMsg = err.response?.data?.message;
        setValid(false);
        setMessage(serverMsg || "Invalid or expired reset link.");
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    }

    verify();
    return () => { isMounted = false };
  }, [token, email]);

  const validatePassword = () => {
    if (password !== confirmPassword) {
      setErrorMsg("Passwords entered do not match. Please try again.");
      return false;
    }
    if (!password || password.length < 8) {
      setErrorMsg("Password must be at least 8 characters long.");
      return false;
    }
    if (!/\d/.test(password)) {
      setErrorMsg("Password must include at least one number.");
      return false;
    }
    if (!/[^A-Za-z0-9]/.test(password)) {
      setErrorMsg("Password must include at least one special character.");
      return false;
    }
    setErrorMsg("");
    return true;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");

    if (!validatePassword()) return;

    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/auth/reset-password`, {
        email,
        token,
        newPassword: password,
      });

      if (res.status === 200) {
        setSuccessMsg(res.data?.message || "Password reset successful. You may now log in.");
        setTimeout(() => {
          navigate('/log-in');
        }, 2200);
      }
    } catch (err) {
      const serverMsg = err.response?.data?.message;
      setErrorMsg(serverMsg || err.message || "Unable to reset password. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  if (loading) return <section style={{ margin: "3rem 5rem" }}><h2>Loading...</h2></section>;

  return (
    <section style={{ margin: "3rem 5rem" }}>
      <h1 className="centered-subtitle">Reset Password</h1>
      <section className="log-in-container">
        {!valid ? (
          <div className="log-in-form">
            <div className="log-in-error">{message || "This reset link is invalid or has expired."}</div>
            <p>If your link has expired, please request a new password reset.</p>
            <p><Link to="/forgot-password">Request a new reset link</Link></p>
          </div>
        ) : (
          <form className="log-in-form" onSubmit={handleSubmit}>
            {errorMsg && <div className="log-in-error">* {errorMsg}</div>}
            {successMsg && <div className="log-in-success">* {successMsg}</div>}

            <label>New Password</label>
            <div>
              <input
                type={toggledPassword ? "text" : "password"}
                className="form-control"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button className="log-in-reveal-button" type="button" onClick={() => setToggledPassword(!toggledPassword)}>
                <i className={`bi ${toggledPassword ? "bi-eye-slash" : "bi-eye"}`}></i>
              </button>
            </div>

            <label>Confirm New Password</label>
            <div>
              <input
                type={toggledPassword ? "text" : "password"}
                className="form-control"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
              <button className="log-in-reveal-button" type="button" onClick={() => setToggledPassword(!toggledPassword)}>
                <i className={`bi ${toggledPassword ? "bi-eye-slash" : "bi-eye"}`}></i>
              </button>
            </div>

            <div className="sign-up-password-criteria-container">
              <p>Your password should meet the following criteria: </p>
              <ul className="sign-up-password-criteria-subcontainer" style={{ fontWeight: "lighter" }}>
                <li>8+ Characters <span style={{ fontWeight: "light", fontStyle: "italic", color: "gray" }}>abc...</span></li>
                <li>1+ Special Character(s) <span style={{ fontWeight: "light", fontStyle: "italic", color: "gray" }}>!@#$...</span></li>
                <li>1+ Number(s) <span style={{ fontWeight: "light", fontStyle: "italic", color: "gray" }}>123...</span></li>
              </ul>
            </div>

            <button type="submit" disabled={loading}>{loading ? 'Resetting password...' : 'Reset Password'}</button>
            <p>Remembered your password? <Link to="/log-in">Log In</Link></p>
          </form>
        )}
      </section>
    </section>
  );
}

export default ResetPassword;
